/// Streaming Morse decoder. Eats `DetectorEvent`s and emits decoded
/// text incrementally. Pure state machine — drive it from any source
/// of (on, end-time) / (off, end-time) transitions.
///
/// The decoder needs a "unit duration" estimate to classify
/// observations:
///
///   On-segment duration:
///     < 2 units → dit (".")
///     ≥ 2 units → dah ("-")
///
///   Off-segment duration:
///     < 2 units   → intra-character gap (no-op)
///     2–5 units   → inter-character gap (decode current pattern)
///     ≥ 5 units   → inter-word gap (decode + emit space)
///
/// The cutoffs are mid-points between ITU's exact spec values
/// (1u dit vs 3u dah → midpoint 2u; 3u intra-char vs 7u inter-word →
/// midpoint 5u). Easier to remember and forgiving of human-keyed
/// sloppiness without breaking on clean machine-generated input.
///
/// The decoder also exposes a `drain()` for callers to nudge it when
/// no audio has arrived for a while — silence is the absence of
/// events, so without a periodic poke the final character of a
/// transmission never gets emitted.

import { ITU_DECODE } from "./code";
import type { DetectorEvent } from "./detector";

export interface StreamingDecoderConfig {
  /// Estimated time unit in milliseconds. `1200 / wpm` (see timing.ts).
  /// 60ms = 20 WPM, 100ms = 12 WPM.
  unitMs: number;
}

export interface StreamingDecoderState {
  /// Morse pattern of the in-progress character (e.g. ".-").
  pattern: string;
  /// `state === "on"`  → audio is currently sounding;
  /// `state === "off"` → audio is silent, possibly between dits/dahs
  ///                     or between characters / words;
  /// `state === "idle"` → never received an event.
  state: "idle" | "on" | "off";
  /// Timestamp (ms) of the most recent on→off transition.
  /// `null` until the first complete on-segment finishes.
  lastOnEndMs: number | null;
  /// Timestamp (ms) of the most recent off→on transition.
  lastStartMs: number | null;
  /// Decoded text emitted so far. Append-only.
  output: string;
}

export const INITIAL_DECODER_STATE: StreamingDecoderState = {
  pattern: "",
  state: "idle",
  lastOnEndMs: null,
  lastStartMs: null,
  output: "",
};

export function handleEvent(
  event: DetectorEvent,
  state: StreamingDecoderState,
  config: StreamingDecoderConfig,
): StreamingDecoderState {
  if (event.type === "start") {
    // Finalise any pending character / word before opening the new on-segment.
    let next = state;
    if (state.state === "off" && state.lastOnEndMs !== null) {
      const offDuration = event.timeMs - state.lastOnEndMs;
      next = finaliseGap(next, offDuration, config);
    }
    return {
      ...next,
      state: "on",
      lastStartMs: event.timeMs,
    };
  }
  // "end" — close the current on-segment.
  if (state.state !== "on" || state.lastStartMs === null) {
    // Stray "end" before any "start" — ignore.
    return { ...state, state: "off", lastOnEndMs: event.timeMs };
  }
  const onDuration = event.timeMs - state.lastStartMs;
  const symbol = onDuration >= config.unitMs * 2 ? "-" : ".";
  return {
    ...state,
    pattern: state.pattern + symbol,
    state: "off",
    lastOnEndMs: event.timeMs,
  };
}

/// Nudge the decoder with a "current time" so that long-silence
/// boundaries get observed even when no events are firing. Idempotent
/// for short silences — only flushes the pattern when the gap since
/// the last on-segment is past the inter-character threshold.
export function drain(
  nowMs: number,
  state: StreamingDecoderState,
  config: StreamingDecoderConfig,
): StreamingDecoderState {
  if (state.state !== "off" || state.lastOnEndMs === null) return state;
  if (state.pattern.length === 0) return state;
  const offDuration = nowMs - state.lastOnEndMs;
  if (offDuration < config.unitMs * 2.5) return state;
  // drain only finalises the in-progress character — never emits a
  // trailing word-space. The word separator is meaningful only as a
  // boundary between two words; appending it at the end of a
  // transmission is just garbage at the tail of the output buffer.
  return finaliseCharacter(state);
}

function finaliseGap(
  state: StreamingDecoderState,
  offDuration: number,
  config: StreamingDecoderConfig,
): StreamingDecoderState {
  let next = state;
  if (offDuration >= config.unitMs * 2.5 && next.pattern.length > 0) {
    next = finaliseCharacter(next);
  }
  if (offDuration >= config.unitMs * 5.5 && !next.output.endsWith(" ")) {
    next = { ...next, output: next.output + " " };
  }
  return next;
}

function finaliseCharacter(state: StreamingDecoderState): StreamingDecoderState {
  if (state.pattern.length === 0) return state;
  const char = ITU_DECODE[state.pattern] ?? "?";
  return { ...state, output: state.output + char, pattern: "" };
}
